/**
 * IndicesTab — shows cards for tracked market indices with sparklines.
 * Clicking a card opens a detail chart with period selectors.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    getIndicesSummary,
    searchFunds,
    getWatchlist,
    addToWatchlist,
    removeFromWatchlist,
} from '../../services/fundsService';
import Sparkline from '../Sparkline';
import IndexDetailModal from './IndexDetailModal';
import FundDetailModal from './FundDetailModal';
import '../../styles/FundsPage.css';

const unwrap = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);
const INDEX_FUND_KEYWORDS = ['index', 'etf', 'nifty', 'sensex', 'nasdaq', 's&p', 'sp 500'];
const INDEX_SYMBOL_FAVOURITES_KEY = 'finlytics:favourite-index-symbols';
const INDEX_SHOW_FAV_ONLY_KEY = 'finlytics:indices-show-favourites-only';
const INDICES_PAGE_SIZE = 24;

const isIndexFund = (item) => {
    const name = (item?.schemeName || '').toLowerCase();
    return INDEX_FUND_KEYWORDS.some((keyword) => name.includes(keyword));
};

const isMetricUnavailable = (val) => {
    if (val == null) return true;
    const num = Number(val);
    if (!Number.isFinite(num)) return true;
    return num === 0;
};

const formatMetric = (val) => (isMetricUnavailable(val) ? 'N/A' : Number(val).toFixed(2));

const IndicesTab = () => {
    const [indices, setIndices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [favourites, setFavourites] = useState([]);
    const [favLoading, setFavLoading] = useState(false);
    const [savingSchemeCode, setSavingSchemeCode] = useState(null);
    const [detailFund, setDetailFund] = useState(null);
    const [indexSearchQuery, setIndexSearchQuery] = useState('');
    const [favouriteIndexSymbols, setFavouriteIndexSymbols] = useState(() => {
        try {
            const raw = localStorage.getItem(INDEX_SYMBOL_FAVOURITES_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed.map((item) => String(item).toUpperCase()) : [];
        } catch {
            return [];
        }
    });
    const [showFavouriteIndicesOnly, setShowFavouriteIndicesOnly] = useState(() => {
        const saved = localStorage.getItem(INDEX_SHOW_FAV_ONLY_KEY);
        if (saved != null) return saved === '1';
        try {
            const rawFav = localStorage.getItem(INDEX_SYMBOL_FAVOURITES_KEY);
            const parsedFav = rawFav ? JSON.parse(rawFav) : [];
            return Array.isArray(parsedFav) && parsedFav.length > 0;
        } catch {
            return false;
        }
    });
    const [currentPage, setCurrentPage] = useState(1);

    const fetchIndices = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const raw = await getIndicesSummary();
            const data = unwrap(raw);
            setIndices(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch indices:', err);
            setError(
                err?.response?.status === 0 || !err?.response
                    ? 'Unable to reach index data service. Ensure the backend is running.'
                    : `Failed to load indices: ${err.message}`
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchIndices();
    }, [fetchIndices]);

    const fetchFavourites = useCallback(async () => {
        setFavLoading(true);
        try {
            const raw = await getWatchlist();
            const data = unwrap(raw);
            const list = Array.isArray(data) ? data : [];
            setFavourites(list.filter(isIndexFund));
        } catch (err) {
            console.error('Failed to fetch favourites:', err);
            setFavourites([]);
        } finally {
            setFavLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFavourites();
    }, [fetchFavourites]);

    useEffect(() => {
        localStorage.setItem(INDEX_SYMBOL_FAVOURITES_KEY, JSON.stringify(favouriteIndexSymbols));
    }, [favouriteIndexSymbols]);

    useEffect(() => {
        localStorage.setItem(INDEX_SHOW_FAV_ONLY_KEY, showFavouriteIndicesOnly ? '1' : '0');
    }, [showFavouriteIndicesOnly]);

    useEffect(() => {
        const q = searchQuery.trim();
        if (q.length < 2) {
            setSearchResults([]);
            return undefined;
        }

        const timer = setTimeout(async () => {
            setSearching(true);
            try {
                const raw = await searchFunds(q);
                const data = unwrap(raw);
                const list = Array.isArray(data) ? data : [];
                setSearchResults(list.filter(isIndexFund).slice(0, 20));
            } catch (err) {
                console.error('Index fund search failed:', err);
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    const handleAddFavourite = async (schemeCode) => {
        setSavingSchemeCode(schemeCode);
        try {
            await addToWatchlist(schemeCode);
            await fetchFavourites();
        } catch (err) {
            console.error('Failed to add favourite:', err);
        } finally {
            setSavingSchemeCode(null);
        }
    };

    const handleRemoveFavourite = async (schemeCode) => {
        setSavingSchemeCode(schemeCode);
        try {
            await removeFromWatchlist(schemeCode);
            await fetchFavourites();
        } catch (err) {
            console.error('Failed to remove favourite:', err);
        } finally {
            setSavingSchemeCode(null);
        }
    };

    const toggleIndexSymbolFavourite = (symbol) => {
        const normalized = String(symbol || '').toUpperCase();
        if (!normalized) return;
        setFavouriteIndexSymbols((prev) => {
            if (prev.includes(normalized)) {
                return prev.filter((item) => item !== normalized);
            }
            return [...prev, normalized];
        });
    };

    const filteredIndices = useMemo(() => {
        const q = indexSearchQuery.trim().toLowerCase();
        return indices.filter((idx) => {
            const symbol = String(idx?.symbol || '').toUpperCase();
            const displaySymbol = String(idx?.displaySymbol || '').toLowerCase();
            const name = String(idx?.name || '').toLowerCase();
            const matchesSearch = !q || name.includes(q) || symbol.toLowerCase().includes(q) || displaySymbol.includes(q);
            const matchesFavouriteFilter = !showFavouriteIndicesOnly || favouriteIndexSymbols.includes(symbol);
            return matchesSearch && matchesFavouriteFilter;
        });
    }, [indices, indexSearchQuery, showFavouriteIndicesOnly, favouriteIndexSymbols]);

    useEffect(() => {
        setCurrentPage(1);
    }, [indexSearchQuery, showFavouriteIndicesOnly]);

    const totalPages = Math.max(1, Math.ceil(filteredIndices.length / INDICES_PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);
    const pageStart = (safePage - 1) * INDICES_PAGE_SIZE;
    const pageEnd = pageStart + INDICES_PAGE_SIZE;
    const pagedIndices = filteredIndices.slice(pageStart, pageEnd);

    const hasActiveIndexFavourites = favouriteIndexSymbols.length > 0;

    if (loading) {
        return <div className="funds-loading">Loading indices…</div>;
    }

    if (error) {
        return (
            <div className="funds-error">
                <p>{error}</p>
                <button className="mf-search-btn" style={{ marginTop: 12 }} onClick={fetchIndices}>
                    Retry
                </button>
            </div>
        );
    }

    if (!indices.length) {
        return (
            <div className="funds-empty">
                <p>No index data available.</p>
            </div>
        );
    }

    return (
        <>
            <div className="mf-section" style={{ marginBottom: '1.25rem' }}>
                <div className="mf-search-row">
                    <input
                        className="mf-search-input"
                        placeholder="Search index funds (e.g. Nifty 50 index, Sensex index, ETF)"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                {(searching || searchResults.length > 0 || searchQuery.trim().length >= 2) && (
                    <div className="mf-search-results">
                        {searching ? (
                            <div className="mf-search-result-item">
                                <span className="mf-result-name">Searching index funds…</span>
                            </div>
                        ) : searchResults.length === 0 ? (
                            <div className="mf-search-result-item">
                                <span className="mf-result-name">No index funds found.</span>
                            </div>
                        ) : (
                            searchResults.map((item) => {
                                const alreadyFav = favourites.some((f) => Number(f.schemeCode) === Number(item.schemeCode));
                                return (
                                    <div key={item.schemeCode} className="mf-search-result-item">
                                        <div>
                                            <div className="mf-result-name">{item.schemeName}</div>
                                            <div className="mf-result-code">{item.schemeCode}</div>
                                        </div>
                                        <button
                                            className="mf-result-add-btn"
                                            disabled={alreadyFav || savingSchemeCode === item.schemeCode}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAddFavourite(item.schemeCode);
                                            }}
                                        >
                                            {alreadyFav ? 'Added' : (savingSchemeCode === item.schemeCode ? 'Adding…' : 'Add to favourites')}
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                <div className="mf-watchlist-section">
                    <h3 className="mf-watchlist-title">Favourite Index Funds</h3>
                    {favLoading ? (
                        <div className="funds-loading" style={{ padding: '1rem 0' }}>Loading favourites…</div>
                    ) : favourites.length === 0 ? (
                        <div className="funds-empty" style={{ padding: '1rem 0' }}>
                            <p>No favourite index funds yet.</p>
                        </div>
                    ) : (
                        <div className="mf-watchlist-table-wrap">
                            <table className="mf-watchlist-table">
                                <thead>
                                    <tr>
                                        <th>Fund</th>
                                        <th>Scheme Code</th>
                                        <th>Latest NAV</th>
                                        <th>NAV Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {favourites.map((fav) => (
                                        <tr key={fav.schemeCode}>
                                            <td>{fav.schemeName || '—'}</td>
                                            <td>{fav.schemeCode}</td>
                                            <td>{fav.latestNav != null ? Number(fav.latestNav).toFixed(2) : '—'}</td>
                                            <td>{fav.navDate || '—'}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '0.4rem' }}>
                                                    <button
                                                        className="mf-details-btn"
                                                        onClick={() => setDetailFund(fav)}
                                                    >
                                                        Details
                                                    </button>
                                                    <button
                                                        className="mf-remove-btn"
                                                        disabled={savingSchemeCode === fav.schemeCode}
                                                        onClick={() => handleRemoveFavourite(fav.schemeCode)}
                                                    >
                                                        {savingSchemeCode === fav.schemeCode ? 'Removing…' : 'Remove'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="indices-toolbar">
                <input
                    className="mf-search-input"
                    placeholder="Search tracked indices (e.g. Nifty, Bank, Pharma)"
                    value={indexSearchQuery}
                    onChange={(e) => setIndexSearchQuery(e.target.value)}
                />
                <button
                    className={`indices-fav-toggle ${showFavouriteIndicesOnly ? 'indices-fav-toggle--active' : ''}`}
                    onClick={() => setShowFavouriteIndicesOnly((prev) => !prev)}
                    type="button"
                >
                    {showFavouriteIndicesOnly ? 'Showing Favourite Indices' : 'Show Only Favourite Indices'}
                </button>
                <div className="indices-filter-meta">
                    {filteredIndices.length} of {indices.length} indices
                </div>
            </div>

            {filteredIndices.length === 0 ? (
                <div className="funds-empty" style={{ padding: '1rem 0 1.75rem' }}>
                    <p>{showFavouriteIndicesOnly ? 'No favourite indices match your search.' : 'No indices match your search.'}</p>
                    {showFavouriteIndicesOnly && !hasActiveIndexFavourites && (
                        <button
                            className="mf-search-btn"
                            style={{ marginTop: 10 }}
                            onClick={() => setShowFavouriteIndicesOnly(false)}
                        >
                            Show All Indices
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="indices-grid">
                        {pagedIndices.map((idx) => {
                            const symbol = String(idx.symbol || '').toUpperCase();
                            const displaySymbol = String(idx.displaySymbol || idx.symbol || '').toUpperCase();
                            const canOpenDetail = Boolean(idx.detailSupported);
                            const isFavouriteIndex = favouriteIndexSymbols.includes(symbol);
                            const valuationUnavailable = [idx.pe, idx.eps, idx.pb].every(isMetricUnavailable);
                            const positive = (idx.change ?? 0) >= 0;
                            const colorClass = positive ? 'index-change-positive' : 'index-change-negative';
                            const sparkColor = positive ? 'var(--success)' : 'var(--danger)';

                            return (
                                <div
                                    key={idx.symbol}
                                    className={`index-card ${canOpenDetail ? '' : 'index-card--static'}`}
                                    onClick={() => {
                                        if (canOpenDetail) setSelectedIndex(idx);
                                    }}
                                    tabIndex={0}
                                    role="button"
                                    aria-label={`${idx.name} — ${idx.last ?? 'N/A'}`}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            if (canOpenDetail) setSelectedIndex(idx);
                                        }
                                    }}
                                >
                                    <div className="index-card-header">
                                        <h3 className="index-card-name">{idx.name}</h3>
                                        <div className="index-card-header-actions">
                                            <button
                                                className={`index-fav-btn ${isFavouriteIndex ? 'index-fav-btn--active' : ''}`}
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleIndexSymbolFavourite(symbol);
                                                }}
                                                aria-label={isFavouriteIndex ? `Remove ${idx.name} from favourite indices` : `Add ${idx.name} to favourite indices`}
                                                title={isFavouriteIndex ? 'Remove from favourite indices' : 'Add to favourite indices'}
                                            >
                                                ★
                                            </button>
                                            <span className="index-card-symbol">{displaySymbol}</span>
                                        </div>
                                    </div>

                                    <div className="index-card-value">
                                        {idx.last != null ? idx.last.toLocaleString('en-IN') : '—'}
                                    </div>

                                    <div className="index-card-change">
                                        <span className={colorClass}>
                                            {positive ? '+' : ''}{idx.change ?? '—'}
                                        </span>
                                        <span className={colorClass}>
                                            ({positive ? '+' : ''}{idx.changePercent ?? '—'}%)
                                        </span>
                                    </div>

                                    <div className="index-card-metrics">
                                        <span className="index-metric-chip">PE: {formatMetric(idx.pe)}</span>
                                        <span className="index-metric-chip">EPS: {formatMetric(idx.eps)}</span>
                                        <span className="index-metric-chip">P/B: {formatMetric(idx.pb)}</span>
                                    </div>

                                    {valuationUnavailable && (
                                        <div className="index-card-updated">
                                            Valuation metrics not published for this index
                                        </div>
                                    )}

                                    {idx.sparkline && idx.sparkline.length > 2 && (
                                        <div className="index-card-sparkline">
                                            <Sparkline values={idx.sparkline} color={sparkColor} height={36} />
                                        </div>
                                    )}

                                    {idx.lastUpdated && (
                                        <div className="index-card-updated">
                                            Updated: {new Date(idx.lastUpdated).toLocaleString()}
                                        </div>
                                    )}

                                    {idx.error && (
                                        <div className="index-card-updated" style={{ color: 'var(--warning)' }}>
                                            ⚠ Data may be delayed
                                        </div>
                                    )}

                                    {!canOpenDetail && (
                                        <div className="index-card-updated">
                                            Detail chart unavailable for this index
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {totalPages > 1 && (
                        <div className="indices-pagination">
                            <button
                                className="indices-page-btn"
                                type="button"
                                disabled={safePage <= 1}
                                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                            >
                                Prev
                            </button>
                            <div className="indices-page-meta">
                                Page {safePage} of {totalPages}
                            </div>
                            <button
                                className="indices-page-btn"
                                type="button"
                                disabled={safePage >= totalPages}
                                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {selectedIndex && (
                <IndexDetailModal
                    index={selectedIndex}
                    onClose={() => setSelectedIndex(null)}
                />
            )}

            {detailFund && (
                <FundDetailModal
                    fund={detailFund}
                    onClose={() => setDetailFund(null)}
                />
            )}
        </>
    );
};

export default IndicesTab;
